import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/efs/PageHeader';

import { fetchOwnBatches } from './api';

const NS = 'portal';

/**
 * Supplier portal — own monthly cases (P0-N5).
 *
 * The list is party-scoped by the backend: a supplier company only ever
 * receives the batches it is linked to (batch supplier organization or
 * inbound source). Each row links into the case detail (progress, quotation,
 * exceptions, report).
 */
export const SupplierBatchesPage = () => {
  const { t } = useTranslation([NS, 'common']);
  const { data, isLoading } = useQuery({
    queryKey: ['supplier-portal', 'batches'],
    queryFn: fetchOwnBatches,
  });

  const rows = data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.portal.batches.title')}
        subtitle={t('efs.portal.batches.subtitle')}
        count={rows.length}
      />

      {isLoading ? (
        <p className="text-sm text-efs-text-muted">
          {t('efs.common.messages.loading', { ns: 'common' })}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-efs-text-muted">{t('efs.portal.batches.empty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('efs.portal.batches.fields.year_month')}</TableHead>
              <TableHead>{t('efs.portal.batches.fields.case_code')}</TableHead>
              <TableHead>{t('efs.portal.batches.fields.status')}</TableHead>
              <TableHead>{t('efs.portal.batches.fields.close_deadline')}</TableHead>
              <TableHead className="text-right">{t('efs.portal.batches.fields.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((b) => (
              <TableRow key={b.monthly_batch_id}>
                <TableCell className="font-medium">{b.year_month}</TableCell>
                <TableCell>
                  <code className="text-xs">{b.case_code ?? '—'}</code>
                </TableCell>
                <TableCell>
                  {t(`efs.portal.batch_status.${b.status}`, { defaultValue: b.status })}
                </TableCell>
                <TableCell>{b.close_deadline ?? '—'}</TableCell>
                <TableCell className="text-right">
                  <Button asChild size="sm" variant="outline">
                    <Link to={`/supplier/batches/${b.monthly_batch_id}`}>
                      {t('efs.portal.batches.open_detail')}
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default SupplierBatchesPage;
